import React, { useEffect, useMemo, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import BackLink from '../BackLink'
import PageHeader from '../PageHeader'
import BlackbaudSlot from '../BlackbaudSlot'

// Final payment step for Blackbaud fundraisers — the counterpart to
// StripePayment, and deliberately the same shape (heading → fields → error →
// submit → back).
//
// Two pieces, both Blackbaud's, mounted from the App-level workflow (see
// useBlackbaudCheckout):
//   - inlineCheckout: one composite iframe carrying the card or bank fields and
//     Blackbaud's cover-the-fees control. Card's tab strip also shows Apple Pay /
//     Google Pay where Blackbaud can render them; Bank's shows a single tab.
//   - completeCheckoutButton: binds to OUR button's id and owns authorization.
//
// The submit button is our element styled like every other v7 primary action,
// but Blackbaud owns its click. There is no onSubmit — the form exists only to
// give the button a sensible enclosing element.
const BlackbaudPayment = ({
  i18n, donation, blackbaud, onBack,
}) => {
  const {
    checkout, ready, processing, buttonLocked, errors, total, handleButtonClick,
  } = blackbaud

  const buttonRef = useRef(null)

  // Stable per-workflow ids. Blackbaud mounts by getElementById and its mount()
  // early-returns when it already believes it owns that id, so these must change
  // whenever the workflow is rebuilt.
  const ids = useMemo(() => {
    const suffix = Math.random().toString(36).slice(2)
    return {
      inline: `blackbaud-inline-${suffix}`,
      buttonContainer: `blackbaud-button-container-${suffix}`,
      button: `blackbaud-button-${suffix}`,
    }
  }, [ checkout ])

  // Capture runs ahead of the SDK's own bubble listener, which is what
  // authorizes — we only veto here. Keyed on ids.button because `key` gives us a
  // fresh node whenever the workflow changes; the handler alone never changes.
  useEffect(() => {
    const button = buttonRef.current
    if (!button) return

    button.addEventListener('click', handleButtonClick, true)
    return () => button.removeEventListener('click', handleButtonClick, true)
  }, [ handleButtonClick, ids.button ])

  const errorMessage = errors?.base?.[0]

  return (
    <form onSubmit={e => e.preventDefault()} noValidate className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />

      {checkout && (
        <BlackbaudSlot component={checkout.inlineCheckoutComponent} id={ids.inline} />
      )}

      {errorMessage && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm"
        >
          {errorMessage}
        </p>
      )}

      <div className="space-y-2">
        {/* buttonLocked is the post-authorization state: Blackbaud has the
            money and a second click would charge again, so the button is
            pointer-inert and out of the tab order rather than merely
            disabled — `disabled` alone would let Blackbaud's own handler,
            which is bound on this element, still see the click. */}
        <button
          key={ids.button}
          ref={buttonRef}
          id={ids.button}
          type="button"
          disabled={processing}
          aria-disabled={buttonLocked || undefined}
          tabIndex={buttonLocked ? -1 : undefined}
          style={buttonLocked ? { pointerEvents: 'none' } : undefined}
          className={[
            'btn btn-primary flex w-full items-center justify-center gap-2',
            buttonLocked && 'opacity-50',
          ].filter(Boolean).join(' ')}
        >
          {processing && <FontAwesomeIcon icon={faCircleNotch} spin />}
          {/* Same stand-in for an order-summary total row that StripePayment
              uses. `total` comes from the hook, not donation, so it includes
              whatever Blackbaud's cover-fees control has added. */}
          {i18n.t('donate_total_recurrence', {
            total: i18n.formatCurrency(total, donation.currency),
            recurrence: i18n.t(`subscription_labels.${donation.state.subscription}.submit`),
          }).trim()}
        </button>
        {checkout && (
          <BlackbaudSlot
            component={checkout.completeCheckoutButtonComponent}
            id={ids.buttonContainer}
            buttonId={ids.button}
          />
        )}
        <BackLink i18n={i18n} onClick={onBack} disabled={processing || !ready} />
      </div>
    </form>
  )
}

export default BlackbaudPayment
